import { Component, computed, DestroyRef, effect, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { isNil } from 'lodash';
import { ConfirmationService, MenuItem, SelectItem } from 'primeng/api';
import { AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { finalize, forkJoin, map } from 'rxjs';
import ObjectId from 'bson-objectid';
import { BreadcrumbService, EnumObjectExtended, EnumValues } from '@card-software/library';
import { AuthService, GlobalMessageUtil, SiteService } from '@card-software/nexus-utilities';
import { getAvailableInventoryWarehouses } from '@app/models/client-purchase-order/client-po-available-inventory';
import {
  ClientPOItemFormGroup,
  ClientPOParty,
  ClientPurchaseOrder,
  ClientPurchaseOrderDetailsDto,
  ClientPurchaseOrderInsert,
  ClientPurchaseOrderItem,
  ClientPurchaseOrderItemDetailsDto,
  NewClientPOItem,
} from '@app/models/client-purchase-order/client-purchase-order';
import { ClientPOStatus } from '@app/models/client-purchase-order/client-purchase-order-enums';
import {
  CLIENT_PO_DEFAULT_TAX_RATE,
  CLIENT_PO_ITEM_FILTERS,
  CLIENT_PO_ITEM_SEARCH_STATUSES,
  CLIENT_PO_RECIPE_SEARCH_STATUSES,
  CLIENT_PO_STATUS_TRANSITIONS,
  CLIENT_PO_TAX_RATE_OPTIONS,
} from '@app/models/client-purchase-order/client-purchase-order.constants';
import { Address } from '@app/models/finance/address';
import { Business } from '@app/models/finance/business';
import { InventoryType } from '@app/models/inventory/inventory-type';
import { ItemMaster, ItemSearchCommand } from '@app/models/item/item-master/item-master';
import { ProductRecipe, ProductRecipeSearch } from '@app/models/product-recipe/product-recipe';
import { ClientPurchaseOrderRoles } from '@app/models/roles/roles-group';
import { BusinessProxyService } from '@app/proxies/finance/business.proxy.service';
import { InventoryTypeProxyService } from '@app/proxies/inventory/inventory-type.proxy.service';
import { ItemMasterProxyService } from '@app/proxies/item/item-master.proxy.service';
import { ProductRecipeProxyService } from '@app/proxies/item/product-recipe.proxy.service';
import { ClientPurchaseOrderProxyService } from '@app/proxies/order/client-purchase-order.proxy.service';
import { EnumService } from '@app/services/enum.service';
import { LocaleHelperService } from '@app/services/locale-helper.service';

@Component({
  selector: 'app-client-purchase-order-details',
  templateUrl: './client-purchase-order-details.component.html',
  styleUrls: ['./client-purchase-order-details.component.scss'],
})
export class ClientPurchaseOrderDetailsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private confirmationService = inject(ConfirmationService);
  private translate = inject(TranslateService);
  private enumService = inject(EnumService);
  private clientPOService = inject(ClientPurchaseOrderProxyService);
  private businessService = inject(BusinessProxyService);
  private itemMasterService = inject(ItemMasterProxyService);
  private inventoryTypeService = inject(InventoryTypeProxyService);
  private localeHelper = inject(LocaleHelperService);
  private productRecipeService = inject(ProductRecipeProxyService);
  private breadcrumbService = inject(BreadcrumbService);

  isLoading = signal(0);
  clientPurchaseOrder = signal<ClientPurchaseOrderDetailsDto | null>(null);
  isNew = signal(false);
  statuses = signal<EnumValues>({});
  statusEnumObject = signal<EnumObjectExtended | undefined>(undefined);
  shippingTermEnums = signal<EnumValues>({});
  shippingTermOptions = computed<SelectItem<number>[]>(() => {
    const values = this.shippingTermEnums();
    return Object.keys(values).map((key) => {
      const numKey = parseInt(key, 10);
      return { label: values[numKey], value: numKey };
    });
  });

  filteredClients = signal<Business[]>([]);
  filteredBrands = signal<Business[]>([]);
  filteredShipTo = signal<Business[]>([]);
  filteredBillTo = signal<Business[]>([]);
  selectedClient = signal<Business | null>(null);
  selectedBrand = signal<Business | null>(null);
  selectedShipTo = signal<Business | null>(null);
  selectedBillTo = signal<Business | null>(null);
  shipToAddresses = signal<Address[]>([]);
  billToAddresses = signal<Address[]>([]);

  searchByRecipeControl = new FormControl(false, { nonNullable: true });
  searchByRecipe = toSignal(this.searchByRecipeControl.valueChanges, { initialValue: false });
  itemOptions = signal<ItemMaster[]>([]);
  recipeSuggestions = signal<ProductRecipe[]>([]);
  itemSearchControl = new FormControl<ItemMaster | null>(null);
  recipeSearchControl = new FormControl<ProductRecipe | null>(null);

  inventoryTypeFilterControl = new FormControl<string | null>(null);
  inventoryTypeOptions = signal<SelectItem<string>[]>([]);
  activeFilters = signal<string[]>([]);
  filterMenuItems = computed<MenuItem[]>(() =>
    CLIENT_PO_ITEM_FILTERS.filter((f) => !this.activeFilters().includes(f.key)).map((f) => ({
      label: this.localeHelper.translateWithFallback(f.labelKey, f.fallbackLabel),
      command: () => this.activeFilters.update((curr) => [...curr, f.key]),
    })),
  );
  unitOfMeasureEnums = signal<EnumValues>({});
  itemsFormArray = new FormArray<FormGroup<ClientPOItemFormGroup>>([]);
  private itemsChanges = toSignal(this.itemsFormArray.valueChanges, { initialValue: [] });
  grandTotal = computed(() => {
    this.itemsChanges();
    return this.itemsFormArray.controls.reduce((sum, fg) => sum + (fg.controls.totalPrice.value || 0), 0);
  });

  selectedLine = signal<FormGroup<ClientPOItemFormGroup> | null>(null);
  selectedLineUomLabel = computed(() => {
    const uom = this.selectedLine()?.controls.uom.value;
    return isNil(uom) ? '' : (this.unitOfMeasureEnums()[uom] ?? '');
  });
  readonly showsAvailableInventory = getAvailableInventoryWarehouses(SiteService.getSelectedSiteId() || '').length > 0;

  readonly taxRateOptions = CLIENT_PO_TAX_RATE_OPTIONS;

  // stream(), not instant(): the i18n loader has not finished when the component is created.
  saveMenuItems = toSignal(
    this.translate
      .stream('SaveAndClose')
      .pipe(map((label: string): MenuItem[] => [{ label, icon: 'pi pi-save', command: () => this.save(true) }])),
    { initialValue: [] },
  );

  canEdit = computed(() => {
    const cpo = this.clientPurchaseOrder();
    return this.hasEditRole() && (cpo === null || cpo.status === ClientPOStatus.Draft);
  });

  clientPOStatusOptions = computed<EnumValues>(() => {
    const all = this.statuses();
    const cpo = this.clientPurchaseOrder();
    if (!cpo) return {};

    const options: EnumValues = {};
    if (all[cpo.status]) options[cpo.status] = all[cpo.status];
    if (!this.hasEditRole()) return options;

    for (const next of CLIENT_PO_STATUS_TRANSITIONS[cpo.status] ?? []) {
      if (all[next]) options[next] = all[next];
    }
    return options;
  });

  statusControl = new FormControl<number>(ClientPOStatus.Draft);
  typeControl = new FormControl<number>(0, Validators.required);
  clientControl = new FormControl<Business | string | null>(null, Validators.required);
  brandControl = new FormControl<Business | string | null>(null);
  clientOrderNumberControl = new FormControl<string | null>(null, Validators.required);
  shippingTermControl = new FormControl<number | null>(null);
  paymentTermCodeControl = new FormControl<string>('', Validators.required);
  shipToControl = new FormControl<Business | string | null>(null, Validators.required);
  shipToAddressControl = new FormControl<number | null>(null, Validators.required);
  billToControl = new FormControl<Business | string | null>(null, Validators.required);
  billToAddressControl = new FormControl<number | null>(null, Validators.required);
  issuedDateControl = new FormControl<Date | null>(null, Validators.required);
  requestedDeliveryDateControl = new FormControl<Date | null>(null);

  constructor() {
    effect(
      () => {
        const enumObj = this.enumService.getEnum('ClientPOStatus', 'orderEnums');
        if (enumObj && Object.keys(enumObj.values).length > 0) {
          this.statusEnumObject.set(enumObj);
          this.statuses.set(enumObj.values);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const uomValues = this.enumService.getEnum('UnitOfMeasure', 'itemEnums').values;
        if (Object.keys(uomValues).length > 0) this.unitOfMeasureEnums.set(uomValues);
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const enumObj = this.enumService.getEnum('WorkOrderShipmentType', 'orderEnums');
        if (enumObj && Object.keys(enumObj.values).length > 0) {
          this.shippingTermEnums.set(enumObj.values);
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.inventoryTypeService
      .getAllInventoryType()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const options = (response?.data ?? []).map((t: InventoryType) => ({
            label: this.localeHelper.getLocalizedValue(t.name),
            value: t.id,
          }));
          this.inventoryTypeOptions.set(options);
        },
      });

    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const id = params['id'];
      if (id === 'new') {
        this.isNew.set(true);
        this.breadcrumbService.setCurrent('Common_New');
        this.issuedDateControl.setValue(new Date());
      } else if (id) {
        this.isNew.set(false);
        this.loadClientPO(id);
      }
    });

    this.statusControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((newStatus) => {
      const cpo = this.clientPurchaseOrder();
      if (newStatus === null || newStatus === undefined || !cpo?.id) return;
      if (newStatus === cpo.status) return;
      this.promptStatusChange(cpo.id, newStatus, cpo.status);
    });
  }

  private hasEditRole(): boolean {
    return (
      AuthService.hasRole(ClientPurchaseOrderRoles.ClientPurchaseOrderAdmin) ||
      AuthService.hasRole(ClientPurchaseOrderRoles.ClientPurchaseOrderEditor)
    );
  }

  private promptStatusChange(id: string, newStatus: number, previousStatus: number): void {
    const leavingDraft = previousStatus === ClientPOStatus.Draft;
    const newStatusLabel = this.statuses()[newStatus] ?? '';
    const confirmLabel = this.localeHelper.translateWithFallback('Confirm', 'Confirm');

    const message = leavingDraft
      ? this.localeHelper.translateWithFallback(
          'Client_PO_StatusConfirm_LeaveDraft',
          'Any unsaved changes will be saved and the order will move to the new status. Continue?',
        )
      : this.localeHelper.translateWithFallback('Client_PO_StatusConfirm_Generic', `Change status to ${newStatusLabel}?`, {
          status: newStatusLabel,
        });

    this.confirmationService.confirm({
      key: 'clientPOStatus',
      header: confirmLabel,
      message,
      icon: 'pi pi-info-circle',
      acceptButtonProps: {
        label: confirmLabel,
      },
      rejectButtonProps: {
        label: this.localeHelper.translateWithFallback('Cancel', 'Cancel'),
        severity: 'secondary',
        outlined: true,
      },
      accept: () => {
        if (leavingDraft) {
          this.saveThenChangeStatus(id, newStatus, previousStatus);
        } else {
          this.changeStatus(id, newStatus, previousStatus);
        }
      },
      reject: () => {
        this.statusControl.setValue(previousStatus, { emitEvent: false });
      },
    });
  }

  private saveThenChangeStatus(id: string, newStatus: number, previousStatus: number): void {
    // The status stays as saved here; changeStatus runs the transition so Order.API validates it and records StatusHistory.
    const payload = this.buildUpdatePayload(previousStatus);
    if (!payload) {
      this.statusControl.setValue(previousStatus, { emitEvent: false });
      return;
    }

    this.incrementLoading();
    this.clientPOService
      .update(payload)
      .pipe(
        finalize(() => this.decrementLoading()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => this.changeStatus(id, newStatus, previousStatus),
        error: () => {
          this.statusControl.setValue(previousStatus, { emitEvent: false });
        },
      });
  }

  private changeStatus(id: string, newStatus: number, previousStatus: number): void {
    this.incrementLoading();
    this.clientPOService
      .changeStatus(id, newStatus)
      .pipe(
        finalize(() => this.decrementLoading()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          if (response.data) {
            // Patch only the status fields: the lightweight response would replace hydrated fields like `client`.
            const current = this.clientPurchaseOrder()!;
            this.clientPurchaseOrder.set({
              ...current,
              status: response.data.status,
              statusHistory: response.data.statusHistory ?? current.statusHistory,
              modifiedBy: response.data.modifiedBy ?? current.modifiedBy,
              modifiedDate: response.data.modifiedDate ?? current.modifiedDate,
            });
            GlobalMessageUtil.success(
              this.localeHelper.translateWithFallback('Success', 'Success'),
              this.localeHelper.translateWithFallback('Client_PO_StatusUpdated', 'Status updated successfully'),
            );
          }
        },
        error: () => {
          this.statusControl.setValue(previousStatus, { emitEvent: false });
        },
      });
  }

  private incrementLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private decrementLoading(): void {
    this.isLoading.update((v) => Math.max(0, v - 1));
  }

  private loadClientPO(id: string): void {
    this.incrementLoading();
    this.clientPOService
      .getDetailsById(id)
      .pipe(
        finalize(() => this.decrementLoading()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          const cpo = response.data;
          if (!cpo) return;
          this.clientPurchaseOrder.set(cpo);
          this.breadcrumbService.setCurrent(cpo.number);
          this.populateForm(cpo);
        },
      });
  }

  private populateForm(cpo: ClientPurchaseOrderDetailsDto): void {
    this.statusControl.setValue(cpo.status, { emitEvent: false });
    this.typeControl.setValue(cpo.type);
    this.clientOrderNumberControl.setValue(cpo.clientOrderNumber ?? null);
    this.shippingTermControl.setValue(cpo.shippingTerm ?? null);
    this.paymentTermCodeControl.setValue(cpo.paymentTermCode);
    this.shipToAddressControl.setValue(cpo.shipToAddressCode);
    this.billToAddressControl.setValue(cpo.billToAddressCode);
    this.issuedDateControl.setValue(cpo.issuedDate ? new Date(cpo.issuedDate) : null);
    this.requestedDeliveryDateControl.setValue(cpo.requestedDeliveryDate ? new Date(cpo.requestedDeliveryDate) : null);

    if (cpo.client) {
      this.selectedClient.set(cpo.client);
      this.clientControl.setValue(cpo.client);
    }
    if (cpo.brand) {
      this.selectedBrand.set(cpo.brand);
      this.brandControl.setValue(cpo.brand);
    }
    if (cpo.shipTo) {
      this.selectedShipTo.set(cpo.shipTo);
      this.shipToControl.setValue(cpo.shipTo);
      this.shipToAddresses.set(cpo.shipTo.addresses ?? []);
    }
    if (cpo.billTo) {
      this.selectedBillTo.set(cpo.billTo);
      this.billToControl.setValue(cpo.billTo);
      this.billToAddresses.set(cpo.billTo.addresses ?? []);
    }

    const selectedLineId = this.selectedLine()?.controls.id.value;
    this.itemsFormArray.clear();
    for (const item of cpo.items) {
      this.itemsFormArray.push(this.createItemFormGroup(item));
    }
    this.selectedLine.set(this.itemsFormArray.controls.find((fg) => fg.controls.id.value === selectedLineId) ?? null);

    if (!this.canEdit()) {
      this.disableForm();
    }
  }

  private disableForm(): void {
    this.typeControl.disable();
    this.clientControl.disable();
    this.brandControl.disable();
    this.clientOrderNumberControl.disable();
    this.shippingTermControl.disable();
    this.paymentTermCodeControl.disable();
    this.shipToControl.disable();
    this.shipToAddressControl.disable();
    this.billToControl.disable();
    this.billToAddressControl.disable();
    this.issuedDateControl.disable();
    this.requestedDeliveryDateControl.disable();
  }

  searchBusiness(event: AutoCompleteCompleteEvent, target: ClientPOParty): void {
    this.businessService
      .search({ name: event.query, businessTypes: [] })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const businesses = response?.data?.items ?? [];
          switch (target) {
            case 'client':
              this.filteredClients.set(businesses);
              break;
            case 'brand':
              this.filteredBrands.set(businesses);
              break;
            case 'shipTo':
              this.filteredShipTo.set(businesses);
              break;
            case 'billTo':
              this.filteredBillTo.set(businesses);
              break;
          }
        },
      });
  }

  onSelectBusiness(event: AutoCompleteSelectEvent, target: ClientPOParty): void {
    const business = event.value as Business;
    switch (target) {
      case 'client':
        this.selectedClient.set(business);
        break;
      case 'brand':
        this.selectedBrand.set(business);
        break;
      case 'shipTo':
        this.selectedShipTo.set(business);
        this.shipToAddresses.set(business.addresses ?? []);
        break;
      case 'billTo':
        this.selectedBillTo.set(business);
        this.billToAddresses.set(business.addresses ?? []);
        break;
    }
  }

  searchItems(event: AutoCompleteCompleteEvent): void {
    const command: ItemSearchCommand = {
      itemCode: event.query,
      name: event.query,
      inventoryTypeReference: this.inventoryTypeFilterControl.value ?? undefined,
      status: CLIENT_PO_ITEM_SEARCH_STATUSES,
    };
    const addedItemCodes = new Set(
      this.itemsFormArray.controls.filter((fg) => !fg.controls.productRecipeNumber.value).map((fg) => fg.controls.itemCode.value),
    );
    this.itemMasterService
      .postItemSearch(command, true, false)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const items = response?.data?.items ?? [];
          this.itemOptions.set(items.filter((i) => !addedItemCodes.has(i.itemCode)));
        },
      });
  }

  removeItemFilter(key: string): void {
    this.activeFilters.update((curr) => curr.filter((k) => k !== key));
    if (key === 'inventoryType') this.inventoryTypeFilterControl.setValue(null);
  }

  onSelectItem(event: AutoCompleteSelectEvent): void {
    const item = event.value as ItemMaster;
    this.itemSearchControl.reset();
    this.addItemRow({ itemCode: item.itemCode, uom: item.unitOfMeasure, quantity: 0, item });
  }

  searchRecipes(event: AutoCompleteCompleteEvent): void {
    const byName: ProductRecipeSearch = { name: event.query, status: CLIENT_PO_RECIPE_SEARCH_STATUSES };
    const byNumber: ProductRecipeSearch = { number: event.query, status: CLIENT_PO_RECIPE_SEARCH_STATUSES };
    const addedRecipeNumbers = new Set(
      this.itemsFormArray.controls.filter((fg) => fg.controls.productRecipeNumber.value).map((fg) => fg.controls.productRecipeNumber.value),
    );
    forkJoin([this.productRecipeService.search(byName), this.productRecipeService.search(byNumber)])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([nameRes, numberRes]) => {
          const nameItems = nameRes?.data?.items ?? [];
          const numberItems = numberRes?.data?.items ?? [];
          const seen = new Set<string>();
          const merged = [...nameItems, ...numberItems].filter((r) => {
            if (!r.outputItemCode || addedRecipeNumbers.has(r.number) || seen.has(r.number)) return false;
            seen.add(r.number);
            return true;
          });
          this.recipeSuggestions.set(merged);
        },
      });
  }

  onSelectRecipe(event: AutoCompleteSelectEvent): void {
    const recipe = event.value as ProductRecipe;
    this.recipeSearchControl.reset();
    if (!recipe.outputItemCode) {
      return;
    }
    this.addItemRow({
      itemCode: recipe.outputItemCode,
      uom: recipe.outputUOM,
      quantity: recipe.baseQuantity,
      productRecipeNumber: recipe.number,
      productRecipe: recipe,
    });
  }

  private addItemRow(line: NewClientPOItem): void {
    const fg = this.createItemFormGroup({
      ...line,
      id: new ObjectId().toHexString(),
      unitPrice: 0,
      totalPrice: 0,
      taxRate: CLIENT_PO_DEFAULT_TAX_RATE,
    });
    this.itemsFormArray.push(fg);
    this.selectedLine.set(fg);
  }

  deleteItem(index: number): void {
    if (this.itemsFormArray.at(index) === this.selectedLine()) {
      this.selectedLine.set(null);
    }
    this.itemsFormArray.removeAt(index);
  }

  private createItemFormGroup(item: ClientPurchaseOrderItemDetailsDto): FormGroup<ClientPOItemFormGroup> {
    const name = item.productRecipe?.name || this.localeHelper.getLocalizedValue(item.item?.itemDisplayName) || item.itemCode;

    const fg = new FormGroup<ClientPOItemFormGroup>({
      id: new FormControl(item.id, { nonNullable: true }),
      itemCode: new FormControl(item.itemCode, { nonNullable: true, validators: [Validators.required] }),
      productRecipeNumber: new FormControl(item.productRecipeNumber ?? null),
      name: new FormControl(name, { nonNullable: true }),
      uom: new FormControl(item.uom),
      quantity: new FormControl(item.quantity, { nonNullable: true, validators: [Validators.required, Validators.min(0.01)] }),
      unitPrice: new FormControl(item.unitPrice, { nonNullable: true, validators: [Validators.required, Validators.min(0)] }),
      taxRate: new FormControl(item.taxRate, { nonNullable: true }),
      totalPrice: new FormControl(item.totalPrice, { nonNullable: true }),
    });

    fg.controls.quantity.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.recalculateItemTotal(fg));
    fg.controls.unitPrice.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.recalculateItemTotal(fg));
    fg.controls.taxRate.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.recalculateItemTotal(fg));

    return fg;
  }

  private recalculateItemTotal(fg: FormGroup<ClientPOItemFormGroup>): void {
    const qty = fg.controls.quantity.value || 0;
    const price = fg.controls.unitPrice.value || 0;
    const taxRate = fg.controls.taxRate.value || 0;
    const total = qty * price * (1 + taxRate);
    fg.controls.totalPrice.setValue(parseFloat(total.toFixed(2)), { emitEvent: false });
  }

  save(closeAfter = false): void {
    if (this.isNew()) {
      this.createClientPO(closeAfter);
    } else {
      this.updateClientPO(closeAfter);
    }
  }

  private createClientPO(closeAfter: boolean): void {
    if (!this.validateBeforeSave()) return;

    const insert: ClientPurchaseOrderInsert = {
      status: this.statusControl.value ?? ClientPOStatus.Draft,
      type: this.typeControl.value ?? 0,
      clientReference: this.selectedClient()!.id,
      brandReference: this.selectedBrand()?.id,
      clientOrderNumber: this.clientOrderNumberControl.value ?? undefined,
      shippingTerm: this.shippingTermControl.value ?? undefined,
      paymentTermCode: this.paymentTermCodeControl.value ?? '',
      shipToReference: this.selectedShipTo()!.id,
      shipToAddressCode: this.shipToAddressControl.value ?? 0,
      billToReference: this.selectedBillTo()!.id,
      billToAddressCode: this.billToAddressControl.value ?? 0,
      issuedDate: this.issuedDateControl.value?.toISOString() ?? new Date().toISOString(),
      requestedDeliveryDate: this.requestedDeliveryDateControl.value?.toISOString(),
      items: this.collectItems(),
    };

    this.incrementLoading();
    this.clientPOService
      .create(insert)
      .pipe(
        finalize(() => this.decrementLoading()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          const poNumber = response.data?.number ?? '';
          GlobalMessageUtil.success(
            this.localeHelper.translateWithFallback('Success', 'Success'),
            this.localeHelper.translateWithFallback('Client_PO_Created', `Created ${poNumber}`, { number: poNumber }),
          );
          if (closeAfter) {
            this.cancel();
          } else if (response.data?.id) {
            // Same-route navigation re-fires the queryParams subscription, reloading the PO under its new id.
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: { id: response.data.id },
              queryParamsHandling: 'merge',
            });
          }
        },
      });
  }

  private updateClientPO(closeAfter: boolean): void {
    const payload = this.buildUpdatePayload();
    if (!payload) return;

    this.incrementLoading();
    this.clientPOService
      .update(payload)
      .pipe(
        finalize(() => this.decrementLoading()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          GlobalMessageUtil.success(
            this.localeHelper.translateWithFallback('Success', 'Success'),
            this.localeHelper.translateWithFallback('Client_PO_Updated', 'Updated successfully'),
          );
          if (closeAfter) {
            this.cancel();
          } else {
            this.loadClientPO(payload.id);
          }
        },
      });
  }

  private buildUpdatePayload(statusOverride?: number): ClientPurchaseOrder | null {
    const cpo = this.clientPurchaseOrder();
    if (!cpo || !this.validateBeforeSave()) return null;

    return {
      ...cpo,
      status: statusOverride ?? this.statusControl.value ?? cpo.status,
      type: this.typeControl.value ?? cpo.type,
      clientReference: this.selectedClient()!.id,
      brandReference: this.selectedBrand()?.id,
      clientOrderNumber: this.clientOrderNumberControl.value ?? undefined,
      shippingTerm: this.shippingTermControl.value ?? undefined,
      paymentTermCode: this.paymentTermCodeControl.value ?? cpo.paymentTermCode,
      shipToReference: this.selectedShipTo()!.id,
      shipToAddressCode: this.shipToAddressControl.value ?? cpo.shipToAddressCode ?? 0,
      billToReference: this.selectedBillTo()!.id,
      billToAddressCode: this.billToAddressControl.value ?? cpo.billToAddressCode ?? 0,
      issuedDate: this.issuedDateControl.value?.toISOString() ?? cpo.issuedDate,
      requestedDeliveryDate: this.requestedDeliveryDateControl.value?.toISOString(),
      items: this.collectItems(),
    };
  }

  private collectItems(): ClientPurchaseOrderItem[] {
    return this.itemsFormArray.controls.map((fg) => ({
      id: fg.controls.id.value,
      itemCode: fg.controls.itemCode.value,
      productRecipeNumber: fg.controls.productRecipeNumber.value ?? undefined,
      uom: fg.controls.uom.value ?? 0,
      quantity: fg.controls.quantity.value,
      unitPrice: fg.controls.unitPrice.value,
      taxRate: fg.controls.taxRate.value,
      totalPrice: fg.controls.totalPrice.value,
    }));
  }

  private validateBeforeSave(): boolean {
    const warning = this.localeHelper.translateWithFallback('Warning', 'Warning');
    if (this.itemsFormArray.length === 0) {
      GlobalMessageUtil.warn(warning, this.localeHelper.translateWithFallback('Client_PO_AddOneItem', 'Add at least one item'));
      return false;
    }
    if (this.clientOrderNumberControl.invalid) {
      GlobalMessageUtil.warn(
        warning,
        this.localeHelper.translateWithFallback('Client_PO_ClientOrderNumberRequired', 'Client Order Number is required'),
      );
      return false;
    }
    if (!this.selectedClient()?.id || !this.selectedShipTo()?.id || !this.selectedBillTo()?.id) {
      GlobalMessageUtil.warn(
        warning,
        this.localeHelper.translateWithFallback('Client_PO_PartiesRequired', 'Client, Ship To, and Bill To are required'),
      );
      return false;
    }
    return true;
  }

  cancel(): void {
    this.router.navigate(['/client-purchase-orders']);
  }
}
